import React from 'react';
import { Link } from 'react-router-dom';

import { Avatar, Box, Typography } from '@mui/material';

import { ShowFnOutput, useModal } from 'mui-modal-provider';

import { ConversationItem, OfferCategory, Reservation, ShippingStatus } from '../../graphql/API';
import { useAppSelector } from '../../redux/hooks';
import Icons from '../../themes/icons';
import { formatNumber } from '../../utils/formatter';
import { VENUE_MANUAL_TAGS } from '../../utils/venue-tags';
import Button from '../Button/Button';
import { BaseDialogProps } from '../Dialogs/Base';
import CancelReservationDialog from '../Dialogs/CancelReservation/CancelReservationDialog';
import ConfirmShipmentDialog from '../Dialogs/ConfirmShipment/ConfirmShipmentDialog';
import { EditReservationDateAndTimeDialog } from '../Dialogs/EditReservationDateAndTime/EditReservationDateAndTimeDialog';
import StatusLabel from '../StatusLabel/StatusLabel';

type Props = {
  conversation: ConversationItem;
  reservationDetailsRefetch?: () => void;
  reservationDetail?: Reservation;
};

export const ChatHeader: React.FC<Props> = ({
  conversation: { firstname, lastname, images, instagramHandle, instagramFollowers, toUserId },
  reservationDetail,
  reservationDetailsRefetch,
}) => {
  const venueState = useAppSelector((state) => state.venue.value);

  const { showModal } = useModal();
  const editDateAndTimeModalRef = React.useRef<ShowFnOutput<BaseDialogProps>>();
  const cancelReservationModalRef = React.useRef<ShowFnOutput<BaseDialogProps>>();
  const shipmentReservationModalRef = React.useRef<ShowFnOutput<BaseDialogProps>>();

  const onMarkShipmentConfirmDialogHandle = () => {
    shipmentReservationModalRef.current = showModal(ConfirmShipmentDialog, {
      modalRef: shipmentReservationModalRef,
      reservationDetail,
      refetchReservationDetails: reservationDetailsRefetch,
    });
  };

  const onEditReservationDateAndTimeDialogHandle = () => {
    editDateAndTimeModalRef.current = showModal(EditReservationDateAndTimeDialog, {
      modalRef: editDateAndTimeModalRef,
      reservationDetail,
      refetchReservationDetails: reservationDetailsRefetch,
    });
  };

  const onCancelReservationDialogHandle = () => {
    cancelReservationModalRef.current = showModal(CancelReservationDialog, {
      modalRef: cancelReservationModalRef,
      reservationDetail,
      refetchReservationDetails: reservationDetailsRefetch,
    });
  };

  const offerCategory = reservationDetail?.offer?.category || reservationDetail?.offerCategory;

  const showReservationButtons =
    (offerCategory &&
      offerCategory === OfferCategory.beauty &&
      reservationDetail.offer?.confirmWithCall) ||
    false;

  const [, toUserID] = toUserId.split('-');

  return (
    <Box
      sx={{
        height: '72px',
        paddingX: '20px',
        bgcolor: '#F9F9F9',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '18px' }}>
        <Avatar src={images?.thumbnail || ''} alt={firstname || 'user-picture'} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
          <Typography
            sx={{ fontSize: '15px', fontWeight: 500, color: '#1E55A1' }}
            component={Link}
            to={`/users/${toUserID}`}
          >
            {firstname} {lastname}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {instagramHandle && (
              <Typography
                sx={{ fontSize: '13px', fontWeight: 500, textDecoration: 'none', color: '#1E55A1' }}
                component={'a'}
                href={`https://instagram.com/${instagramHandle}`}
                target='_blank'
              >
                @{instagramHandle}
              </Typography>
            )}
            <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>
              {formatNumber(+(instagramFollowers || 0))} followers
            </Typography>
          </Box>
        </Box>
      </Box>

      {venueState?.tags?.includes(VENUE_MANUAL_TAGS.Online) &&
        reservationDetail?.shippingStatus === ShippingStatus.waiting && (
          <Box
            display={'flex'}
            alignItems={'center'}
            gap={'68px'}
            sx={{ borderLeft: '1px solid #EBE8E8', px: '24px' }}
          >
            <Box display={'flex'} flexDirection={'column'}>
              <Typography
                color={'#949BA5'}
                fontSize={'12px'}
                textTransform={'uppercase'}
                fontWeight={600}
              >
                Shipping status
              </Typography>
              <StatusLabel
                type='shippingStatus'
                status={reservationDetail?.shippingStatus}
                emptyBackground
              />
            </Box>

            <Button variant='outlined' sx={{ px: 2.5 }} onClick={onMarkShipmentConfirmDialogHandle}>
              Mark as Shipped
            </Button>
          </Box>
        )}

      {!venueState?.tags?.includes(VENUE_MANUAL_TAGS.Online) && showReservationButtons && (
        <Box display={'flex'} alignItems={'center'} gap={'8px'}>
          <Button
            startIcon={<Icons.CalendarIcon />}
            variant='outlined'
            sx={{ px: 2.5 }}
            onClick={onEditReservationDateAndTimeDialogHandle}
          >
            Edit reservation
          </Button>

          <Button
            startIcon={<Icons.DeleteIcon />}
            variant='outlined'
            color='error'
            sx={{ px: 2.5 }}
            onClick={onCancelReservationDialogHandle}
          >
            Cancel
          </Button>
        </Box>
      )}
    </Box>
  );
};
